import axios, { AxiosError, AxiosResponse } from 'axios'
import { StorageService, LocalStorageService, SessionStorageService, StorageType } from './StorageService'

export abstract class AbstractService {
  private sessionStorageService: StorageService = new SessionStorageService()
  private localStorageService: StorageService = new LocalStorageService()

  protected parseList<T>(response: AxiosResponse) {
    if (response.status !== 200) throw Error(response.statusText)
    if (!response.data) return []

    let list: T[] = response.data
    if (typeof list !== 'object') {
      list = []
    }
    return list
  }

  protected parseString(response: AxiosResponse): string {
    if (response.status !== 200) throw Error(response.statusText)
    if (!response.data) return ''
    return response.data as string
  }

  protected parseObject<T>(response: AxiosResponse): T | null {
    if (response.status !== 200) throw Error(response.statusText)
    if (!response.data) return {} as T

    let data: T = response.data
    if (typeof data !== 'object') {
      data = {} as T
    }
    return data
  }

  protected handleAxiosErrors(error: AxiosError, model: string) {
    /**
     * This is a technical error, targeting the developers.
     * You should always log it here (lowest level). This serves the developer.
     * If I want to propogate this back to the callers,
     * I should determine how to propogate it out and if I want to transform it.
     */
    console.error(`Developer Error: Async Data Error: ${error.message}`);

    /**
     *  How do I feel about errors in this path?
     * Log the error here,
     * and let the caller know an error occurred,
     * but dont change the return type
     */
    throw new Error(`Oh no! We're unable to fetch the ${model}`);

    /**
     * Throw errors or return them?
     *
     * We could pass this back every time.
     * the argument here is you can avoid try/catch everywhere but you instead have to package the error.
     * interface Message {
     *   response: any;
     *   error: string;
     * }
     *
     * return the error object to the caller, to be examined
     * return {response, error};
     */
  }

  private getStore(type: StorageType): StorageService {
    return type == StorageType.Local ? this.localStorageService : this.sessionStorageService
  }

  protected async getLookupData<T>(key: string, store: StorageType, apiCallback: (...args: any[]) => Promise<T[]>, ...args: any[]): Promise<T[]> {
    const storageService = this.getStore(store)
    let data = storageService.retrieve(key)

    if (data == null){
      data = await apiCallback(args)
      if (data) {
        storageService.store(key, data)
      }
    }
    return data
  }

  protected async getLookupObject<T>(key: string, store: StorageType, apiCallback: (...args: any[]) => Promise<T>, ...args: any[]): Promise<T | null> {
    const storageService = this.getStore(store)
    let data = storageService.retrieve(key)

    if (data == null){
      data = await apiCallback(args)
      if (data) {
        storageService.store(key, data)
      }
    }
    return data
  }
}
